import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Power, MapPin, Clock, DollarSign, Star, Navigation, AlertCircle, Bell, Check, X, Upload, FileText, MessageCircle, PhoneCall, Loader2, Users, ChevronRight, ChevronLeft } from 'lucide-react';
import { CurrencyCircleDollar, Wallet, Car, Star as PhosphorStar } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { GlassCard, GoldButton, StatusBadge, LoadingSpinner, EmptyState } from '../components/common/GlassComponents';
import { PlatformMap } from '../map/components/PlatformMap';
import { CAMERA_MODES } from '../map/camera/CameraConfig';
import { Layout } from '../components/layout/Layout';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { TourGuideButton } from '../components/common/TourGuideButton';
import { tourGuide } from '../services/TourGuideService';
import { DriverDashboardSkeleton } from '../components/common/PageSkeletons';
import { useWebSocket } from '../context/WebSocketContext';
import { useWatchPosition } from '../map/hooks/useGeolocation';
import { haptics } from '../utils/haptics';
import { systemNotification } from '../services/SystemNotificationService';
import { routeCache } from '../map/route/RouteCache';
import { RouteProgress } from '../map/route/RouteProgress';
import { toast } from 'sonner';
import { App } from '@capacitor/app';
import { useRef } from 'react';
import { AppCache } from '../utils/AppCache';

import { ActiveTripQueue } from '../components/driver/ActiveTripQueue';
import { SharedRideRequestPanel } from '../components/driver/SharedRideRequestPanel';
import { RideCard } from '../components/driver/RideCard';
import { SlideButton } from '../components/ui/slide-button';

const DriverDashboard = () => {
  const navigate = useNavigate();
  const { user, api, driverProfile, refreshDriverProfile, logout } = useAuth();
  const { theme, accentColor, ACCENT_COLORS } = useTheme();


  const { newRideRequest, removedSharedRideId, clearRideRequest, clearRemovedSharedRideId, sendLocationUpdate, requestHardRecalculation, rideUpdate, clearRideUpdate, routeQueueUpdate, clearRouteQueueUpdate, driverTripSegments, driverLocation, isConnected } = useWebSocket();

  const [routeQueue, setRouteQueue] = useState([]);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [recalcTrigger, setRecalcTrigger] = useState(0);
  const hardCooldownRef = useRef(0);

  const [isOnline, setIsOnline] = useState(false);
  const [isTogglingOnline, setIsTogglingOnline] = useState(false);
  const [activeRide, setActiveRide] = useState(() => {
    try {
      const cached = localStorage.getItem('driver_active_ride');
      return cached ? JSON.parse(cached) : null;
    } catch (e) {
      return null;
    }
  });
  const [activeRides, setActiveRides] = useState(() => {
    try {
      const cached = localStorage.getItem('driver_active_rides');
      return cached ? JSON.parse(cached) : [];
    } catch (e) {
      return [];
    }
  });
  const [pendingRides, setPendingRides] = useState([]);
  const [masterSharedRidePool, setMasterSharedRidePool] = useState([]);
  const [routesData, setRoutesData] = useState([]);
  
  // Passenger Colors for Shared Rides
  const PASSENGER_COLORS = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ec4899', '#ef4444'];
  
  const getColorForRideId = (rideId) => {
    if (!rideId) return '#3b82f6';
    const index = activeRides.findIndex(r => r.id === rideId);
    if (index === -1) return '#3b82f6';
    return PASSENGER_COLORS[index % PASSENGER_COLORS.length];
  };
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  
  // Auto-launch interactive driver tour on first visit when not in active ride
  useEffect(() => {
    if (!activeRide && !localStorage.getItem('jride_driver_tour_seen')) {
      const timer = setTimeout(() => {
        const activeAccentHex = ACCENT_COLORS?.[accentColor]?.hex || '#3B82F6';
        tourGuide.startDriverDashboardTour(activeAccentHex, theme);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [activeRide, accentColor, theme, ACCENT_COLORS]);

  // Auto-launch incoming request tour for first-time drivers
  useEffect(() => {
    if (pendingRides.length > 0 && !localStorage.getItem('jride_tour_driver_request_seen')) {
      const timer = setTimeout(() => {
        const activeAccentHex = ACCENT_COLORS?.[accentColor]?.hex || '#3B82F6';
        tourGuide.startDriverIncomingRequestTour(activeAccentHex, theme);
      }, 700);
      return () => clearTimeout(timer);
    }
  }, [pendingRides.length, accentColor, theme, ACCENT_COLORS]);
  
  const getPassengerState = (status) => {
    if (['requested', 'accepted', 'driver_accepted', 'driver_en_route_to_pickup'].includes(status)) return 'WAITING PICKUP';
    if (['driver_arrived'].includes(status)) return 'DRIVER ARRIVED';
    if (['passenger_boarding', 'ride_started', 'ongoing', 'ride_in_progress', 'arriving_destination'].includes(status)) return 'INSIDE VEHICLE';
    if (['completed', 'ended'].includes(status)) return 'COMPLETED';
    if (['cancelled', 'declined'].includes(status)) return 'CANCELLED';
    return 'UNKNOWN';
  };

  const getRideId = (r) => r?.id || r?._id || (typeof r === 'string' ? r : null);

  // Monotonic lifecycle guards:
  // terminalRideIdsRef tracks completed/cancelled rides so stale GET requests never restore them.
  const terminalRideIdsRef = useRef(new Set());
  // inFlightAcceptIdsRef tracks rides being accepted optimistically so background fetchData does not wipe them out.
  const inFlightAcceptIdsRef = useRef(new Set());

  // Calculate dynamically based on Passenger States
  const occupiedPassengers = useMemo(() => {
    return activeRides.reduce((sum, ride) => {
      const state = getPassengerState(ride.status);
      if (['WAITING PICKUP', 'DRIVER ARRIVED', 'INSIDE VEHICLE'].includes(state)) {
        return sum + (ride.passenger_count || 1);
      }
      return sum;
    }, 0);
  }, [activeRides]);

  const availableSeats = useMemo(() => {
    return (driverProfile?.vehicle_capacity || 4) - occupiedPassengers;
  }, [driverProfile?.vehicle_capacity, occupiedPassengers]);

  const incomingSharedRequests = useMemo(() => {
    if (driverProfile?.accepts_shared_rides === false) return [];
    if (availableSeats <= 0) return [];
    
    // Filter out rides that are ALREADY active for this driver so accepted rides never remain on the side panel
    const activeRideIds = new Set(activeRides.map(r => getRideId(r)));
    return masterSharedRidePool.filter(r => !activeRideIds.has(getRideId(r)));
  }, [masterSharedRidePool, activeRides, driverProfile?.accepts_shared_rides, availableSeats]);
  const [uploading, setUploading] = useState(false);
  
  // Bottom Sheet State
  const [isSheetExpanded, setIsSheetExpanded] = useState(false);

  // Watch position when online
  const { startWatching, stopWatching, isWatching, error: watchError } = useWatchPosition(
    useCallback((location) => {
      setCurrentLocation(location);
      if (isOnline) {
        if (isConnected) {
          sendLocationUpdate(location.lat, location.lng, location.heading, location.speed);
        } else {
          // Fallback to HTTP POST only if WebSocket is disconnected
          api.post('/drivers/location', {
            latitude: location.lat,
            longitude: location.lng,
            heading: location.heading || 0,
            speed: location.speed || 0
          }).catch(console.error);
        }
      }
    }, [isOnline, isConnected, sendLocationUpdate, api]),
    { enableHighAccuracy: true }
  );

  const refreshActiveRide = useCallback(async () => {
    try {
      const [activeRes, tripRes] = await Promise.all([
        api.get('/rides/driver/active'),
        api.get('/rides/driver/trip').catch(() => ({ data: { route_queue: [] } }))
      ]);
      const serverRides = (activeRes.data || []).filter(r => 
        r.status !== 'cancelled' && 
        r.status !== 'completed' && 
        r.status !== 'ride_completed' && 
        r.status !== 'ride_archived' && 
        !terminalRideIdsRef.current.has(getRideId(r))
      );

      setActiveRides(prev => {
        const inFlightRides = prev.filter(r => inFlightAcceptIdsRef.current.has(getRideId(r)) && !serverRides.some(sr => getRideId(sr) === getRideId(r)));
        const merged = [...serverRides, ...inFlightRides];
        if (merged.length === 0) {
          setRouteQueue([]);
          setRoutesData([]);
          localStorage.removeItem('driver_active_rides');
          localStorage.removeItem('driver_active_ride');
          setActiveRide(null);
        } else {
          localStorage.setItem('driver_active_rides', JSON.stringify(merged));
          localStorage.setItem('driver_active_ride', JSON.stringify(merged[0]));
          setActiveRide(prevActive => {
            if (!prevActive) return merged[0];
            const matching = merged.find(r => getRideId(r) === getRideId(prevActive));
            return matching || merged[0];
          });
          if (tripRes?.data?.route_queue) {
            setRouteQueue(tripRes.data.route_queue);
            if (Array.isArray(tripRes.data.full_driver_segments)) {
              setRoutesData(tripRes.data.full_driver_segments);
            }
          }
        }
        return merged;
      });
    } catch (err) {
      console.error("Error refreshing active ride:", err);
    }
  }, [api]);

  const fetchData = useCallback(async () => {
    try {
      const [activeRes, earningsRes, tripRes] = await Promise.all([
        api.get('/rides/driver/active'),
        api.get('/drivers/earnings'),
        api.get('/rides/driver/trip').catch(() => ({ data: { route_queue: [] } }))
      ]);

      const serverRides = (activeRes.data || []).filter(r => 
        r.status !== 'cancelled' && 
        r.status !== 'completed' && 
        r.status !== 'ride_completed' && 
        r.status !== 'ride_archived' && 
        !terminalRideIdsRef.current.has(getRideId(r))
      );

      setActiveRides(prev => {
        const inFlightRides = prev.filter(r => inFlightAcceptIdsRef.current.has(getRideId(r)) && !serverRides.some(sr => getRideId(sr) === getRideId(r)));
        const merged = [...serverRides, ...inFlightRides];
        if (merged.length === 0) {
          setRouteQueue([]);
          setRoutesData([]);
          localStorage.removeItem('driver_active_rides');
          localStorage.removeItem('driver_active_ride');
          setActiveRide(null);
        } else {
          localStorage.setItem('driver_active_rides', JSON.stringify(merged));
          localStorage.setItem('driver_active_ride', JSON.stringify(merged[0]));
          setActiveRide(prevActive => {
            if (!prevActive) return merged[0];
            const matching = merged.find(r => getRideId(r) === getRideId(prevActive));
            return matching || merged[0];
          });
        }
        return merged;
      });

      setEarnings(earningsRes.data);
      if (tripRes?.data) {
        if (tripRes.data.route_queue) setRouteQueue(tripRes.data.route_queue);
        if (tripRes.data.full_driver_segments && Array.isArray(tripRes.data.full_driver_segments) && tripRes.data.full_driver_segments.length > 0) {
          setRoutesData(tripRes.data.full_driver_segments);
        }
      }
      
      const pendingRes = await api.get('/rides/pending');
      if (pendingRes.data) {
        const allPending = pendingRes.data || [];
        setPendingRides(allPending);
        setMasterSharedRidePool(allPending.filter(r => r.ride_mode === 'shared'));
      }
      
      await refreshDriverProfile();
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  // NOTE: driverProfile intentionally excluded — including it causes fetchData to
  // recreate on every profile update, triggering a stale GET that clobbers optimistic UI state.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, navigate, refreshDriverProfile]);

  // Initial data load on mount — stable, runs exactly once
  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle Capacitor App State (Background -> Foreground)
  useEffect(() => {
    let listener = null;
    const setupListener = async () => {
      listener = await App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          console.log('App resumed, refreshing driver dashboard data...');
          fetchData();
        }
      });
    };
    setupListener();

    return () => {
      if (listener) {
        listener.remove();
      }
    };
  }, [fetchData]);

  // On WebSocket reconnect: only refresh the route queue and earnings.
  // Do NOT call fetchData() here — it fetches activeRides from the backend and
  // clobbers any optimistic UI state that was set before the reconnect.
  useEffect(() => {
    if (isConnected) {
      api.get('/rides/driver/trip')
        .then(res => { if (res?.data?.route_queue) setRouteQueue(res.data.route_queue); })
        .catch(() => {});
    }
  }, [isConnected, api]);

  useEffect(() => {
    if (!rideUpdate) return;
    
    if (rideUpdate.type === 'driver_arrived') {
      setActiveRides(prev => prev.map(r => getRideId(r) === rideUpdate.ride_id ? { ...r, status: 'driver_arrived' } : r));
      if (activeRide && getRideId(activeRide) === rideUpdate.ride_id) {
        setActiveRide(prev => ({ ...prev, status: 'driver_arrived' }));
      }
      clearRideUpdate();
    } else if (rideUpdate.type === 'started' || rideUpdate.type === 'ride_started') {
      setActiveRides(prev => prev.map(r => getRideId(r) === rideUpdate.ride_id ? { ...r, status: 'ongoing' } : r));
      if (activeRide && getRideId(activeRide) === rideUpdate.ride_id) {
        setActiveRide(prev => ({ ...prev, status: 'ongoing' }));
      }
      clearRideUpdate();
    } else if (rideUpdate.type === 'completed') {
      setActiveRides(prev => {
        const remaining = prev.filter(r => getRideId(r) !== rideUpdate.ride_id);
        if (remaining.length === 0) {
          localStorage.removeItem('driver_active_rides');
          localStorage.removeItem('driver_active_ride');
          setActiveRide(null);
          setRouteQueue([]);
          setRoutesData([]);
        } else {
          localStorage.setItem('driver_active_rides', JSON.stringify(remaining));
          localStorage.setItem('driver_active_ride', JSON.stringify(remaining[0]));
          if (activeRide && getRideId(activeRide) === rideUpdate.ride_id) {
            setActiveRide(remaining[0]);
          }
        }
        return remaining;
      });
      clearRideUpdate();
    } else if (rideUpdate.type === 'rating_received') {
      toast.success(`You received a ${rideUpdate.rating}-star rating!`);
      setEarnings(prev => ({ ...prev, rating: rideUpdate.new_average }));
      refreshDriverProfile();
      clearRideUpdate();
    } else if (rideUpdate.type === 'cancelled' || rideUpdate.type === 'ride_cancelled' || rideUpdate.type === 'declined') {
        const cancelledId = rideUpdate.ride_id || rideUpdate.ride?.id;
        setActiveRides(prev => {
          const remaining = prev.filter(r => getRideId(r) !== cancelledId);
          if (remaining.length === 0) {
            localStorage.removeItem('driver_active_rides');
            localStorage.removeItem('driver_active_ride');
            setActiveRide(null);
            setRouteQueue([]);
            setRoutesData([]);
          } else {
            localStorage.setItem('driver_active_rides', JSON.stringify(remaining));
            localStorage.setItem('driver_active_ride', JSON.stringify(remaining[0]));
            if (activeRide && getRideId(activeRide) === cancelledId) {
              setActiveRide(remaining[0]);
            }
            // INSTANT OPTIMISTIC PRUNING OF QUEUE AND ROUTE SEGMENTS
            setRouteQueue(prevQueue => prevQueue.filter(st => st.ride_id !== cancelledId));
            setRoutesData(prevRoutes => prevRoutes.filter(seg => {
              const segId = String(seg.stopId || seg.rideId || '');
              return !segId.includes(cancelledId);
            }));
          }
          return remaining;
        });
        const wasSelfCancelled = cancelledId && terminalRideIdsRef.current.has(cancelledId);
        if (cancelledId) {
          terminalRideIdsRef.current.add(cancelledId);
        }
        if (!wasSelfCancelled) {
          toast.error(rideUpdate.message || 'Student cancelled the ride');
          try { haptics.tripCompleted(); } catch (e) {}
        }
        setPendingRides(prev => prev.filter(r => getRideId(r) !== cancelledId));
        setMasterSharedRidePool(prev => prev.filter(r => getRideId(r) !== cancelledId));
        clearRideUpdate();
      } else if (rideUpdate.type === 'accepted') {
        setPendingRides(prev => prev.filter(r => getRideId(r) !== getRideId(rideUpdate.ride)));
        setMasterSharedRidePool(prev => prev.filter(r => getRideId(r) !== getRideId(rideUpdate.ride)));
        clearRideUpdate();
    }
  }, [rideUpdate, refreshDriverProfile, activeRide, refreshActiveRide, clearRideUpdate]);

  useEffect(() => {
    if (routeQueueUpdate) {
      setRouteQueue(routeQueueUpdate);
      clearRouteQueueUpdate();
    }
  }, [routeQueueUpdate, clearRouteQueueUpdate]);

  useEffect(() => {
    if (driverTripSegments && Array.isArray(driverTripSegments) && driverTripSegments.length > 0) {
      setRoutesData(driverTripSegments);
    }
  }, [driverTripSegments]);

  // Handle real-time synchronization for shared pool
  useEffect(() => {
    if (removedSharedRideId) {
      setMasterSharedRidePool(prev => prev.filter(r => getRideId(r) !== removedSharedRideId));
      setPendingRides(prev => prev.filter(r => getRideId(r) !== removedSharedRideId));
      clearRemovedSharedRideId();
    }
  }, [removedSharedRideId, clearRemovedSharedRideId]);

  useEffect(() => {
    if (driverProfile) {
      if (typeof driverProfile.is_online === 'boolean') {
        setIsOnline(driverProfile.is_online);
      }
      if (driverProfile.current_location && driverProfile.current_location.lat) {
        setCurrentLocation(driverProfile.current_location);
      } else {
        setCurrentLocation(prev => prev || { lat: -14.294532832852411, lng: 28.54970049702143, heading: 0 });
      }
    }
  }, [driverProfile?.is_online, driverProfile?.id]);

  useEffect(() => {
    if (isOnline && !isWatching) {
      startWatching();
    } else if (!isOnline && isWatching) {
      stopWatching();
    }
  }, [isOnline, isWatching, startWatching, stopWatching]);

  useEffect(() => {
    if (watchError && isOnline) {
      if (watchError === 'PERMISSION_DENIED' || String(watchError).toLowerCase().includes('denied')) {
        toast.error('Location permission is blocked. Please tap the site settings / lock icon in your browser address bar and allow Location access.', { id: 'driver-geo-denied', duration: 7000 });
      }
    }
  }, [watchError, isOnline]);

  // Handle new incoming ride requests
  useEffect(() => {
    if (newRideRequest) {
      const reqId = getRideId(newRideRequest);
      const isShared = !newRideRequest.ride_mode || String(newRideRequest.ride_mode).toLowerCase() === 'shared';
      
      // Shared rides also go into the Master Pool
      if (isShared) {
        setMasterSharedRidePool(prev => {
          if (!prev.some(r => getRideId(r) === reqId)) {
            return [newRideRequest, ...prev];
          }
          return prev;
        });
      }
      
      // All rides go into the Pending Rides list so they show up on the Idle Home Page
      setPendingRides(prev => {
        if (!prev.some(r => getRideId(r) === reqId)) {
          return [newRideRequest, ...prev];
        }
        return prev;
      });

      systemNotification.notifyNewRideRequest(newRideRequest.pickup_name || newRideRequest.pickup_address);
      
      clearRideRequest();
    }
  }, [newRideRequest, clearRideRequest]);

  // Periodic background refresh for pending rides while online
  useEffect(() => {
    if (!isOnline) return;
    const interval = setInterval(async () => {
      try {
        const pendingRes = await api.get('/rides/pending');
        if (pendingRes.data) {
          const allPending = pendingRes.data || [];
          setPendingRides(allPending);
          setMasterSharedRidePool(allPending.filter(r => r.ride_mode === 'shared'));
        }
      } catch (err) {
        // silent fail on background interval
      }
    }, 8000);
    return () => clearInterval(interval);
  }, [isOnline, api]);

  const driverLocRef = useRef(null);
  useEffect(() => {
    driverLocRef.current = currentLocation;
    
    // Phase 3.2.5C: Route Recalculation Watchdog
    if (currentLocation && routeQueue.length > 0 && routesData.length > 0) {
      if (isRecalculating) return;
      if (Date.now() < hardCooldownRef.current) return;
      
      const driverFirstSegment = routesData.find(r => r.rideId.startsWith('driver-'));
      if (driverFirstSegment && driverFirstSegment.geometry) {
        const deviationStatus = RouteProgress.checkDeviationLevel(currentLocation, driverFirstSegment.geometry);
        if (deviationStatus === 'OFF_ROUTE') {
           const logicalStatus = RouteProgress.evaluateLogicalThreshold(currentLocation, routeQueue);
           if (logicalStatus === 'SOFT') {
              setIsRecalculating(true);
              setRecalcTrigger(v => v + 1);
              // Unlock after 5 seconds to prevent spamming while API resolves
              setTimeout(() => setIsRecalculating(false), 5000);
           } else if (logicalStatus === 'HARD') {
              hardCooldownRef.current = Date.now() + 30000; // 30 second cooldown
              if (requestHardRecalculation) {
                requestHardRecalculation();
              }
           }
        }
      }
    }
  }, [currentLocation, routeQueue, routesData, isRecalculating, requestHardRecalculation]);

  useEffect(() => {
    if (driverTripSegments && driverTripSegments.length > 0) {
      setRoutesData(driverTripSegments);
    } else if (activeRides && activeRides.length > 0) {
      const fallbackSegments = activeRides.map((r, i) => {
        const geo = r.verified_geometry || r.route_geometry || [];
        if (geo && geo.length > 2) {
          return {
            rideId: r.id || `ride-${i}`,
            geometry: geo,
            color: getColorForRideId(r.id),
            status: 'active',
            isActiveLeg: (i === 0),
            isNextLeg: (i === 0)
          };
        }
        return null;
      }).filter(Boolean);
      if (fallbackSegments.length > 0) {
        setRoutesData(fallbackSegments);
      }
    } else if (activeRides.length === 0) {
      setRoutesData([]);
    }
  }, [driverTripSegments, activeRides]);

  const handleToggleOnline = async () => {
    if (isTogglingOnline) return;
    if (!driverProfile || driverProfile.needs_setup || !driverProfile.plate_number || !driverProfile.vehicle_model) {
      toast.error('Please complete your vehicle setup first');
      navigate('/driver/register');
      return;
    }

    if (!driverProfile?.is_approved) {
      toast.error('Your driver account is pending admin approval');
      return;
    }

    if (driverProfile?.wallet_status === 'restricted' || (driverProfile?.total_commission_due || 0) >= (driverProfile?.max_commission_owed_limit || 500)) {
      toast.error('Wallet restricted. Please remit commission to Admin to go online.');
      return;
    }

    setIsTogglingOnline(true);
    const targetState = !isOnline;
    setIsOnline(targetState); // Optimistic UI update

    try {
      const response = await api.post('/drivers/toggle-online');
      const actualState = Boolean(response.data.is_online);
      setIsOnline(actualState);
      
      // Update local cache so background refreshes never flip the switch back
      if (driverProfile) {
        driverProfile.is_online = actualState;
        try {
          const cached = JSON.parse(localStorage.getItem('mulu_driver_cache') || '{}');
          cached.is_online = actualState;
          localStorage.setItem('mulu_driver_cache', JSON.stringify(cached));
        } catch (e) {}
      }

      if (actualState) {
        // Driver came online: immediately fetch pending rides so pool populates instantly!
        fetchData();
      } else {
        // Driver went offline: clear pending pool
        setPendingRides([]);
        setMasterSharedRidePool([]);
      }

      toast.success(actualState ? 'You are now online' : 'You are now offline');
    } catch (error) {
      setIsOnline(!targetState); // Revert on failure
      toast.error(error.response?.data?.detail || 'Failed to toggle status');
    } finally {
      setIsTogglingOnline(false);
    }
  };

  const handleAcceptRide = async (rideId) => {
    inFlightAcceptIdsRef.current.add(rideId);
    terminalRideIdsRef.current.delete(rideId);

    const fakeAcceptedRide = pendingRides.find(r => getRideId(r) === rideId) || { id: rideId, status: 'accepted' };
    const targetRide = { ...fakeAcceptedRide, status: 'accepted' };

    const initialGeo = targetRide.verified_geometry || targetRide.route_geometry || [];
    if (initialGeo.length > 0) {
      setRoutesData([{
        rideId: rideId,
        geometry: initialGeo,
        color: '#3b82f6',
        status: 'active',
        isActiveLeg: true
      }]);
    }

    setActiveRide(prev => prev || targetRide);
    setActiveRides(prev => {
      const updated = prev.some(r => getRideId(r) === rideId) ? prev : [...prev, targetRide];
      localStorage.setItem('driver_active_rides', JSON.stringify(updated));
      return updated;
    });
    localStorage.setItem('driver_active_ride', JSON.stringify(targetRide));
    setPendingRides(prev => prev.filter(r => getRideId(r) !== rideId));
    clearRideRequest();
    toast.success('Ride accepted!');

    try {
      const response = await api.post(`/rides/${rideId}/accept`);
      if (response.data) {
        setActiveRide(response.data);
        setActiveRides(prev => {
          const updated = prev.map(r => getRideId(r) === rideId ? response.data : r);
          if (!updated.some(r => getRideId(r) === rideId)) updated.push(response.data);
          localStorage.setItem('driver_active_rides', JSON.stringify(updated));
          return updated;
        });
        localStorage.setItem('driver_active_ride', JSON.stringify(response.data));
        if (response.data.verified_geometry && Array.isArray(response.data.verified_geometry) && response.data.verified_geometry.length > 0) {
          setRoutesData(prev => (prev && prev.length > 0 && prev[0].geometry.length > 0) ? prev : [{
            rideId: rideId,
            geometry: response.data.verified_geometry,
            color: '#3b82f6',
            status: 'active',
            isActiveLeg: true
          }]);
        }
      }
      // Non-blocking trip sync in background
      api.get('/rides/driver/trip').then(tripRes => {
        if (tripRes.data?.route_queue) setRouteQueue(tripRes.data.route_queue);
        if (tripRes.data?.full_driver_segments && Array.isArray(tripRes.data.full_driver_segments) && tripRes.data.full_driver_segments.length > 0) {
          setRoutesData(tripRes.data.full_driver_segments);
        }
      }).catch(() => {});
    } catch (error) {
      console.error('Error accepting ride:', error);
      toast.error(error.response?.data?.detail || 'Failed to accept ride');
      fetchData(); // Rollback on error
    } finally {
      inFlightAcceptIdsRef.current.delete(rideId);
    }
  };

  const handleAcceptSharedRide = async (rideId) => {
    inFlightAcceptIdsRef.current.add(rideId);
    terminalRideIdsRef.current.delete(rideId);

    const fakeAcceptedRide = masterSharedRidePool.find(r => getRideId(r) === rideId) || pendingRides.find(r => getRideId(r) === rideId) || { id: rideId, status: 'accepted' };
    const targetRide = { ...fakeAcceptedRide, status: 'accepted' };

    const initialGeo = targetRide.verified_geometry || targetRide.route_geometry || [];
    if (initialGeo.length > 0) {
      setRoutesData(prev => {
        if (!prev || prev.length === 0) {
          return [{
            rideId: rideId,
            geometry: initialGeo,
            color: '#3b82f6',
            status: 'active',
            isActiveLeg: true
          }];
        }
        // Append additional passenger leg without wiping existing route
        if (prev.some(seg => seg.rideId === rideId)) return prev;
        return [
          ...prev,
          {
            rideId: rideId,
            geometry: initialGeo,
            color: '#10b981',
            status: 'active',
            isActiveLeg: false
          }
        ];
      });
    }

    setActiveRide(prev => prev || targetRide);
    setActiveRides(prev => {
      const updated = prev.some(r => getRideId(r) === rideId) ? prev : [...prev, targetRide];
      localStorage.setItem('driver_active_rides', JSON.stringify(updated));
      return updated;
    });
    localStorage.setItem('driver_active_ride', JSON.stringify(targetRide));
    setMasterSharedRidePool(prev => prev.filter(r => getRideId(r) !== rideId));
    setPendingRides(prev => prev.filter(r => getRideId(r) !== rideId));
    toast.success('Shared ride accepted!');
    
    try {
      const response = await api.post(`/rides/${rideId}/accept`);
      if (response.data) {
        setActiveRide(response.data);
        setActiveRides(prev => {
          const updated = prev.map(r => getRideId(r) === rideId ? response.data : r);
          if (!updated.some(r => getRideId(r) === rideId)) updated.push(response.data);
          localStorage.setItem('driver_active_rides', JSON.stringify(updated));
          return updated;
        });
        localStorage.setItem('driver_active_ride', JSON.stringify(response.data));
        if (response.data.verified_geometry && Array.isArray(response.data.verified_geometry) && response.data.verified_geometry.length > 0) {
          setRoutesData(prev => (prev && prev.length > 0 && prev[0].geometry.length > 0) ? prev : [{
            rideId: rideId,
            geometry: response.data.verified_geometry,
            color: '#3b82f6',
            status: 'active',
            isActiveLeg: true
          }]);
        }
      }
      // Non-blocking trip sync in background
      api.get('/rides/driver/trip').then(tripRes => {
        if (tripRes.data?.route_queue) setRouteQueue(tripRes.data.route_queue);
        if (tripRes.data?.full_driver_segments && Array.isArray(tripRes.data.full_driver_segments) && tripRes.data.full_driver_segments.length > 0) {
          setRoutesData(tripRes.data.full_driver_segments);
        }
      }).catch(() => {});
    } catch (error) {
      console.error('Error accepting shared ride:', error);
      toast.error(error.response?.data?.detail || 'Failed to accept ride');
      fetchData(); // Revert on error
    } finally {
      inFlightAcceptIdsRef.current.delete(rideId);
    }
  };

  const handleDeclineSharedRide = (rideId) => {
    setMasterSharedRidePool(prev => prev.filter(r => r.id !== rideId));
    setPendingRides(prev => prev.filter(r => r.id !== rideId));
  };

  const handleDeclineRide = () => {
    clearRideRequest();
  };

  const handleDriverArrived = async (targetRide = activeRide) => {
    if (!targetRide) return;
    const targetId = getRideId(targetRide);
    if (!targetId) return;

    // Optimistic UI update: instantly reflect arrived status across activeRides & activeRide
    setActiveRides(prev => prev.map(r => getRideId(r) === targetId ? { ...r, status: 'driver_arrived' } : r));
    if (activeRide && getRideId(activeRide) === targetId) {
      setActiveRide(prev => ({ ...prev, status: 'driver_arrived' }));
    }
    toast.success('Passenger notified of your arrival');

    try {
      await api.post(`/rides/${targetId}/arrived`);
    } catch (error) {
      console.error('Failed to update arrived status asynchronously:', error);
      // Keep optimistic UI locked on driver_arrived (zero-flicker policy)
    }
  };

  const handleStartRide = async (targetRide = activeRide) => {
    if (!targetRide) return;
    const targetId = getRideId(targetRide);
    if (!targetId) return;

    // Optimistic UI update: instantly reflect started status
    setActiveRides(prev => prev.map(r => getRideId(r) === targetId ? { ...r, status: 'ongoing' } : r));
    if (activeRide && getRideId(activeRide) === targetId) {
      setActiveRide(prev => ({ ...prev, status: 'ongoing' }));
    }
    setRouteQueue(prev => prev.filter(stop => !(stop.ride_id === targetId && stop.stop_type === 'pickup')));
    toast.success('Ride started');

    try {
      await api.post(`/rides/${targetId}/start`);
    } catch (error) {
      console.error('Failed to start ride asynchronously:', error);
      // Keep optimistic UI locked on ongoing (zero-flicker policy)
    }
  };

  const handleCompleteRide = async (targetRide = activeRide) => {
    if (!targetRide) return;
    const targetId = getRideId(targetRide);
    if (!targetId) return;

    terminalRideIdsRef.current.add(targetId);
    inFlightAcceptIdsRef.current.delete(targetId);

    // Optimistic UI update: instantly transition to remaining student ride if any
    setActiveRides(prev => {
      const remaining = prev.filter(r => getRideId(r) !== targetId);
      if (remaining.length === 0) {
        localStorage.removeItem('driver_active_rides');
        localStorage.removeItem('driver_active_ride');
        setActiveRide(null);
        setRouteQueue([]);
        setRoutesData([]);
      } else {
        localStorage.setItem('driver_active_rides', JSON.stringify(remaining));
        localStorage.setItem('driver_active_ride', JSON.stringify(remaining[0]));
        if (activeRide && getRideId(activeRide) === targetId) {
          setActiveRide(remaining[0]);
        }
      }
      return remaining;
    });
    setRouteQueue(prev => prev.filter(stop => stop.ride_id !== targetId));
    AppCache.remove('history');
    AppCache.remove('recent_rides');
    toast.success('Ride completed!');

    try {
      const response = await api.post(`/rides/${targetId}/complete`);
      if (response.data?.fare) {
        toast.success(`Fare: K${response.data.fare}`);
      }
      fetchData(); // Refresh earnings
    } catch (error) {
      console.error('Failed to complete ride asynchronously:', error);
    }
  };

  const handleCancelRide = async (targetRide = activeRide) => {
    if (!targetRide) return;
    const targetId = getRideId(targetRide);
    if (!targetId) return;

    terminalRideIdsRef.current.add(targetId);
    inFlightAcceptIdsRef.current.delete(targetId);

    // Optimistic UI update: filter out cancelled ride immediately without dumping driver to home page
    setActiveRides(prev => {
      const remaining = prev.filter(r => getRideId(r) !== targetId);
      if (remaining.length === 0) {
        localStorage.removeItem('driver_active_rides');
        localStorage.removeItem('driver_active_ride');
        setActiveRide(null);
      } else {
        localStorage.setItem('driver_active_rides', JSON.stringify(remaining));
        localStorage.setItem('driver_active_ride', JSON.stringify(remaining[0]));
        if (activeRide && getRideId(activeRide) === targetId) {
          setActiveRide(remaining[0]);
        }
      }
      return remaining;
    });
    setRouteQueue(prev => prev.filter(stop => stop.ride_id !== targetId && getRideId(stop) !== targetId));
    setRoutesData(prev => prev.filter(segment => segment.ride_id !== targetId));
    AppCache.remove('history');
    AppCache.remove('recent_rides');
    toast.info('Ride cancelled');

    try {
      await api.post(`/rides/${targetId}/cancel`);
      refreshActiveRide();
    } catch (error) {
      console.error('Failed to cancel ride:', error);
      toast.error('Failed to cancel ride');
      refreshActiveRide();
    }
  };

  const openNavigation = (location) => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${location.lat},${location.lng}`;
    window.open(url, '_blank');
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('File too large. Max 2MB allowed.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    try {
      await api.post(`/drivers/documents?doc_type=${type}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} uploaded successfully`);
      refreshDriverProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return <DriverDashboardSkeleton />;
  }

  // 1. Incomplete Setup State (Needs Vehicle Info & Docs - only for unapproved drivers who lack info)
  if (driverProfile && !driverProfile.is_approved && (driverProfile.needs_setup || !driverProfile.plate_number || !driverProfile.vehicle_model)) {
    return (
      <Layout>
        <div className="p-4 md:p-8 max-w-lg mx-auto" data-testid="driver-needs-setup">
          <GlassCard className="p-6 md:p-8 text-center space-y-5 shadow-lg border border-border">
            <div className="w-16 h-16 rounded-3xl bg-amber-500/10 flex items-center justify-center mx-auto shadow-inner">
              <Car weight="duotone" className="w-8 h-8 text-amber-500" />
            </div>
            <div>
              <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-500/10 text-amber-600 dark:text-amber-400 px-3 py-1 rounded-full">
                Action Required
              </span>
              <h2 className="font-heading text-2xl font-bold text-foreground mt-3 mb-1">
                Vehicle Setup Required
              </h2>
              <p className="text-muted-foreground text-xs leading-relaxed max-w-xs mx-auto">
                You have been registered as a driver, but your vehicle credentials and documents have not been submitted yet.
              </p>
            </div>

            <div className="p-4 bg-muted/60 rounded-2xl text-left font-body border border-border space-y-2">
              <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">Required Information:</p>
              <ul className="text-xs text-foreground space-y-1.5 list-disc list-inside">
                <li>Vehicle type, make, model & color</li>
                <li>Official vehicle plate number</li>
                <li>Passenger seating capacity</li>
                <li>Driver profile photo & license documents</li>
              </ul>
            </div>

            <button
              onClick={() => {
                haptics.tap();
                navigate('/driver/register');
              }}
              className="w-full py-4 bg-black text-white dark:bg-white dark:text-black rounded-2xl font-bold text-sm flex items-center justify-center gap-2 active:scale-98 transition-transform shadow-md"
            >
              Complete Vehicle Setup Now
              <ChevronRight className="w-4 h-4" />
            </button>
          </GlassCard>
        </div>
      </Layout>
    );
  }

  // 2. Pending Approval State (Details submitted, awaiting admin review)
  if (driverProfile && !driverProfile.is_approved) {
    return (
      <Layout>
        <div className="p-4 md:p-8 max-w-lg mx-auto" data-testid="driver-pending">
          <GlassCard className="p-6 md:p-8 text-center space-y-5 shadow-lg border border-border">
            <div className="w-16 h-16 rounded-3xl bg-yellow-500/10 flex items-center justify-center mx-auto shadow-inner">
              <AlertCircle className="w-8 h-8 text-yellow-500" />
            </div>
            <div>
              <span className="text-[10px] font-bold uppercase tracking-wider bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 px-3 py-1 rounded-full">
                Under Review
              </span>
              <h2 className="font-heading text-xl font-bold text-foreground mt-3 mb-1">
                Pending Admin Approval
              </h2>
              <p className="text-muted-foreground text-xs leading-relaxed max-w-xs mx-auto">
                Your vehicle credentials and documents have been submitted and are currently being reviewed by admin.
              </p>
            </div>

            <div className="p-4 bg-muted/60 rounded-2xl text-left font-body border border-border space-y-1">
              <div className="flex items-center justify-between mb-1">
                <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">Submitted Vehicle</p>
                <button 
                  onClick={() => {
                    haptics.tap();
                    navigate('/driver/register');
                  }}
                  className="text-xs text-foreground font-bold hover:underline flex items-center gap-0.5"
                >
                  Edit <ChevronRight className="w-3 h-3" />
                </button>
              </div>
              <p className="text-sm font-bold text-foreground">{driverProfile.vehicle_color} {driverProfile.vehicle_model} ({driverProfile.vehicle_type})</p>
              <p className="text-xs text-gold font-mono font-bold">{driverProfile.plate_number}</p>
            </div>

            <div className="space-y-3 text-left">
              <h3 className="text-foreground font-semibold text-xs flex items-center gap-2">
                <FileText className="w-4 h-4 text-gold" />
                Verification Documents
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <DocumentUploadCard
                  title="Driver's License"
                  type="license"
                  url={driverProfile.verification_docs?.license}
                  onUpload={(e) => handleFileUpload(e, 'license')}
                  uploading={uploading}
                />
                <DocumentUploadCard
                  title="Vehicle Registration"
                  type="registration"
                  url={driverProfile.verification_docs?.registration}
                  onUpload={(e) => handleFileUpload(e, 'registration')}
                  uploading={uploading}
                />
              </div>
            </div>
          </GlassCard>
        </div>
      </Layout>
    );
  }

  // ==========================================
  // ACTIVE RIDE FULL-SCREEN VIEW (DRIVER)
  // ==========================================
  if (activeRide) {
    // Markers Logic
    const mapMarkers = [];
    const bounds = [];
    
    // 1. Always show driver car (falling back to Mulungushi University if GPS is offline)
    const activeDriverLoc = currentLocation || driverProfile?.current_location || { lat: -14.294532832852411, lng: 28.54970049702143, heading: 0 };
    const driversArray = [{ id: 'driver-self', latitude: activeDriverLoc.lat, longitude: activeDriverLoc.lng, heading: activeDriverLoc.heading || 0 }];
    if (activeDriverLoc?.lat) bounds.push([activeDriverLoc.lat, activeDriverLoc.lng]);

    // 2. Loop through routeQueue to show stops in optimized order
    routeQueue.forEach((stop, index) => {
      if (!stop.coordinates || !stop.coordinates.lat) return;
      
      mapMarkers.push({
        lat: stop.coordinates.lat,
        lng: stop.coordinates.lng,
        iconType: stop.stop_type,
        pIndex: `${index + 1}`, // Sequential Stop Numbering
        color: getColorForRideId(stop.ride_id), // Colored by Passenger Ownership
        key: stop.stop_id
      });
      bounds.push([stop.coordinates.lat, stop.coordinates.lng]);
    });

    const getStatusText = () => {
      switch(activeRide.status) {
        case 'accepted': return 'Pick up passenger';
        case 'driver_arrived': return 'Wait for passenger';
        case 'ongoing': return 'Drop off passenger';
        default: return 'Active Ride';
      }
    };

    // Make sure we have a valid target location for navigation
    const targetLocation = activeRide.status === 'requested' || activeRide.status === 'accepted' 
      ? activeRide.pickup_location 
      : activeRide.dropoff_location;

    return (
      <div className="fixed inset-0 bg-background overflow-hidden flex flex-col" data-testid="driver-active-ride">
        {/* Render intelligent shared ride requests here (ONLY for Shared Rides or when Driver has no active private ride) */}
        {(!activeRide || activeRide.ride_mode === 'shared') && (
          <SharedRideRequestPanel 
            requests={incomingSharedRequests}
            onAccept={handleAcceptSharedRide}
            onDecline={handleDeclineSharedRide}
            driverLocation={currentLocation}
          />
        )}
        
        {/* Map Layer */}
        <div className="flex-1 relative z-0">
          {/* Animated Capacity Indicator for Shared Rides */}
          {activeRide.ride_mode === 'shared' && driverProfile?.accepts_shared_rides && (
            <motion.div 
              initial={{ y: -50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="absolute top-6 left-4 z-10"
            >
              <div className="bg-white/90 dark:bg-zinc-900/90 backdrop-blur-md px-4 py-2 rounded-full shadow-lg border border-zinc-200/50 dark:border-zinc-800/50 flex items-center gap-3">
                <div className="relative">
                  <Users className="w-5 h-5 text-emerald-500" />
                  <AnimatePresence>
                    {availableSeats <= 0 && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white dark:border-zinc-900"
                      />
                    )}
                  </AnimatePresence>
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider leading-none mb-0.5">Capacity</span>
                  <div className="flex items-baseline gap-1 leading-none">
                    <motion.span 
                      key={availableSeats}
                      initial={{ y: -10, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      className={`font-heading font-bold text-lg ${availableSeats > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`}
                    >
                      {Math.max(0, availableSeats)}
                    </motion.span>
                    <span className="text-sm font-medium text-muted-foreground">/ {driverProfile?.vehicle_capacity || 4} seats</span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          <PlatformMap
            isDriverView={true}
            cameraMode={CAMERA_MODES.FOLLOW_DRIVER}
            driverLocation={activeDriverLoc}
            center={activeDriverLoc || { lat: -14.294532832852411, lng: 28.54970049702143 }}
            bounds={null}
            zoom={14}
            markers={mapMarkers}
            drivers={driversArray}
            routes={routesData}
            rideStatus={activeRide.status}
            showRouteLine={true}
            interactive={true}
            className="w-full h-full"
          />
          
          {/* Navigation Overlay Button */}
          {targetLocation && (
            <div className="absolute top-4 right-4 z-10 pointer-events-auto">
              <button
                onClick={() => openNavigation(targetLocation)}
                className="w-14 h-14 bg-black text-white rounded-full flex items-center justify-center shadow-2xl active:scale-95 transition-transform border-4 border-black/10"
              >
                <Navigation className="w-6 h-6" />
              </button>
            </div>
          )}
        </div>

        {/* Bottom Sheet Overlay */}
        <motion.div
          className="absolute bottom-0 left-0 right-0 bg-white dark:bg-zinc-950 rounded-t-[32px] shadow-[0_-8px_30px_rgba(0,0,0,0.12)] z-20 flex flex-col max-h-[85vh]"
          animate={{ y: isSheetExpanded ? 0 : "calc(100% - 140px)" }}
          transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
          drag="y"
          dragConstraints={{ top: 0, bottom: 0 }}
          dragElastic={0.2}
          onDragEnd={(e, info) => {
            if (info.offset.y > 50) setIsSheetExpanded(false);
            if (info.offset.y < -50) setIsSheetExpanded(true);
          }}
        >
          {/* Drag Handle Zone */}
          <div 
            className="w-full pt-4 pb-2 flex justify-center cursor-grab active:cursor-grabbing"
            onClick={() => setIsSheetExpanded(!isSheetExpanded)}
          >
            <div className="w-12 h-1.5 bg-zinc-300 dark:bg-zinc-700 rounded-full" />
          </div>

          <div className="px-6 pb-6 pt-2 flex flex-col gap-6 flex-1 overflow-hidden">
            
            {/* Minimized Header Row */}
            <div className="flex flex-col gap-4 text-center cursor-pointer" onClick={() => setIsSheetExpanded(!isSheetExpanded)}>
               <div className="font-bold text-sm tracking-wider text-muted-foreground uppercase">
                 {activeRide.ride_mode === 'shared' ? 'Shared Ride' : 'Private Ride'}
               </div>
               
               {activeRides.length > 1 ? (
                 <div className="flex flex-col items-center gap-1">
                   <h2 className="font-heading text-2xl font-bold text-foreground">
                     {activeRides.reduce((sum, r) => sum + (r.passenger_count || 1), 0)} PASSENGERS
                   </h2>
                   {activeRide?.ride_mode === 'shared' && activeRides.filter(r => ['accepted', 'driver_accepted'].includes(r.status)).length > 0 && (
                     <span className="text-sm font-bold text-gold uppercase tracking-wider">
                       +{activeRides.filter(r => ['accepted', 'driver_accepted'].includes(r.status)).length} WAITING
                     </span>
                   )}
                 </div>
               ) : (
                 <div className="flex flex-col items-center gap-1">
                   <h2 className="font-heading text-2xl font-bold text-foreground">
                     {activeRides[0]?.passenger_count || 1} PASSENGER
                   </h2>
                   {activeRide?.ride_mode === 'shared' && ['accepted', 'driver_accepted'].includes(activeRides[0]?.status) && (
                     <span className="text-sm font-bold text-gold uppercase tracking-wider">
                       +1 WAITING
                     </span>
                   )}
                 </div>
               )}
            </div>

            {/* EXPANDED CONTENT */}
            <div id="tour-driver-route-queue" className="flex flex-col gap-8 overflow-y-auto flex-1 pt-4 pb-12 pr-2">
                  {(() => {
                    // Extract unique ride_ids in order of their appearance in routeQueue
                    const routeOrderIds = [];
                    if (routeQueue && routeQueue.length > 0) {
                      routeQueue.forEach(stop => {
                        if (stop.ride_id && !routeOrderIds.includes(stop.ride_id)) {
                          routeOrderIds.push(stop.ride_id);
                        }
                      });
                    }

                    // Sort activeRides to strictly follow routeQueue operational sequence
                    const sortedActiveRides = [...activeRides].sort((a, b) => {
                      const idxA = routeOrderIds.indexOf(a.id);
                      const idxB = routeOrderIds.indexOf(b.id);
                      if (idxA !== -1 && idxB !== -1) return idxA - idxB;
                      if (idxA !== -1) return -1;
                      if (idxB !== -1) return 1;
                      return 0;
                    });

                    return sortedActiveRides.map((ride, index) => {
                      const color = getColorForRideId(ride.id);
                      const pIndex = `P${index + 1}`;
                    const rideStatusStr = ['accepted', 'driver_accepted'].includes(ride.status) ? 'WAITING' :
                                          ['driver_arrived', 'passenger_boarding'].includes(ride.status) ? 'ARRIVED' :
                                          ['ride_started', 'ride_in_progress', 'ongoing'].includes(ride.status) ? 'ACTIVE' :
                                          ride.status.toUpperCase();
                                          
                    return (
                      <div key={ride.id} className="flex flex-col gap-4 pb-8 border-b border-zinc-100 dark:border-zinc-800 last:border-0 last:pb-0">
                        {/* Student Header */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <span 
                              className="text-white font-bold text-sm px-2.5 py-1 rounded-md"
                              style={{ backgroundColor: color }}
                            >
                              {pIndex}
                            </span>
                            <span className="font-bold text-lg text-foreground">{ride.student?.name || 'Passenger'}</span>
                          </div>
                          <span className={`text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider ${rideStatusStr === 'ACTIVE' ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400' : 'bg-muted text-muted-foreground'}`}>
                            {rideStatusStr}
                          </span>
                        </div>
                        
                        {/* Trip Info */}
                        <div className="flex flex-col gap-2">
                          <div className="flex justify-between items-start">
                            <span className="text-sm text-muted-foreground uppercase tracking-wider font-bold">Pickup:</span>
                            <span className="text-sm font-medium text-foreground text-right max-w-[60%] truncate">{ride.pickup_location?.address}</span>
                          </div>
                          <div className="flex justify-between items-start">
                            <span className="text-sm text-muted-foreground uppercase tracking-wider font-bold">Destination:</span>
                            <span className="text-sm font-medium text-foreground text-right max-w-[60%] truncate">{ride.dropoff_location?.address}</span>
                          </div>
                          <div className="flex justify-between items-start mt-2">
                            <span className="text-lg font-bold text-gold uppercase tracking-wider">K{ride.driver_earning?.toFixed(2)}</span>
                          </div>
                        </div>

                        {/* Quick Contact Icons */}
                        <div className="flex gap-2 mt-2">
                           <button 
                             className="flex-1 py-3 rounded-xl bg-muted text-foreground border border-border flex items-center justify-center gap-2 active:scale-95 transition-transform"
                             onClick={() => navigate(`/ride/chat/${ride.id}`)}
                           >
                             <MessageCircle className="w-5 h-5" />
                             <span className="font-bold">Chat</span>
                           </button>
                           <button 
                             className="flex-1 py-3 rounded-xl bg-muted text-foreground border border-border flex items-center justify-center gap-2 active:scale-95 transition-transform"
                             onClick={() => window.location.href = `tel:${ride.student?.phone}`}
                           >
                             <PhoneCall className="w-5 h-5" />
                             <span className="font-bold">Call</span>
                           </button>
                        </div>
                        
                        {/* Classic Actions Group */}
                        <div className="flex flex-col gap-3 pt-2">
                           {['accepted', 'driver_accepted'].includes(ride.status) && (
                              <div className="flex gap-2">
                                 <button onClick={() => handleCancelRide(ride)} className="w-1/3 py-4 bg-red-500/10 hover:bg-red-500/20 text-red-500 border border-red-500/20 rounded-xl font-bold active:scale-95 transition-transform">
                                   Cancel
                                 </button>
                                 <button onClick={() => handleDriverArrived(ride)} className="w-2/3 py-4 bg-primary text-primary-foreground rounded-xl font-bold active:scale-95 transition-transform">
                                   I've Arrived
                                 </button>
                              </div>
                           )}
                           
                           {ride.status === 'driver_arrived' && (
                              <button onClick={() => handleStartRide(ride)} className="w-full py-4 bg-blue-600 text-white rounded-xl font-bold active:scale-95 shadow-lg shadow-blue-600/20 transition-transform">
                                Start Ride
                              </button>
                           )}

                    {['ride_started', 'ride_in_progress', 'ongoing'].includes(ride.status) && (
                              <button onClick={() => handleCompleteRide(ride)} className="w-full py-4 bg-emerald-500 text-white rounded-xl font-bold active:scale-95 shadow-lg shadow-emerald-500/20 transition-transform">
                                Complete Ride
                              </button>
                           )}
                        </div>
                      </div>
                     );
                   });
                  })()}
            </div>
          </div>
        </motion.div>
      </div>
    );
  }


    // ==========================================
    // STANDARD DASHBOARD VIEW (NO ACTIVE RIDE)
    // ==========================================
    return (
      <Layout>
        <div className="p-4 md:p-8 pb-36 md:pb-28 space-y-6 max-w-4xl mx-auto" data-testid="driver-dashboard">
        {/* Header with Online Toggle */}
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 sm:gap-3 mb-1 flex-wrap">
              <h1 className="font-heading text-xl sm:text-2xl md:text-3xl font-bold text-foreground">
                {isOnline ? 'Ready for Rides' : 'You\'re Offline'}
              </h1>
              {earnings && (
                <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-gold/10 border border-gold/20 shadow-sm shrink-0" title="Driver Rating">
                  <PhosphorStar weight="duotone" className="w-4 h-4 text-gold fill-gold" />
                  <span className="font-heading font-bold text-foreground text-sm">
                    {earnings.rating?.toFixed(1) || '5.0'}
                  </span>
                </div>
              )}
              <TourGuideButton tourType="driver" />
            </div>
            <p className="text-muted-foreground text-sm">
              {isOnline ? 'Waiting for ride requests...' : 'Go online to start accepting rides'}
            </p>
          </div>
          <div id="tour-driver-slide-btn" className="flex flex-col items-end gap-2 shrink-0">
            <SlideButton 
              isOnline={isOnline}
              onToggle={handleToggleOnline}
              disabled={isTogglingOnline}
            />
          </div>
        </div>

        {/* Commission Debt Restriction Alert */}
        {(driverProfile?.wallet_status === 'restricted' || (driverProfile?.total_commission_due || 0) >= (driverProfile?.max_commission_owed_limit || 500)) && (
          <div className="bg-red-500/10 border border-red-500/50 rounded-xl p-4 flex items-start gap-4 mb-6 animate-pulse">
            <AlertCircle className="w-6 h-6 text-red-500 shrink-0 mt-1" />
            <div>
              <h3 className="font-heading font-bold text-foreground text-lg">Commission Debt Limit Reached</h3>
              <p className="text-muted-foreground text-sm mb-2">
                You have reached the maximum accumulated commission limit (K{(driverProfile?.total_commission_due || 0).toFixed(2)} / K{(driverProfile?.max_commission_owed_limit || 500).toFixed(2)}).
                You cannot accept new rides until you remit payment to the Admin.
              </p>
              <div className="bg-black/20 rounded px-2 py-1 inline-block">
                <span className="text-xs text-red-400 font-mono">
                  Limit: K{(driverProfile?.max_commission_owed_limit || 500).toFixed(2)} • Please contact Admin to settle payment
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Commission Debt Accumulation Card */}
        <GlassCard className="p-4 mb-6" hover={false}>
          <div className="flex justify-between items-center mb-2">
            <div className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-gold" />
              <span className="font-semibold text-foreground text-sm">Platform Commission Owed</span>
            </div>
            <span className={`font-mono font-bold text-sm ${(driverProfile?.total_commission_due || 0) >= ((driverProfile?.max_commission_owed_limit || 500) * 0.9) ? 'text-red-400' : 'text-gold'}`}>
              K{(driverProfile?.total_commission_due || 0).toFixed(2)} / K{(driverProfile?.max_commission_owed_limit || 500).toFixed(2)}
            </span>
          </div>
          <div className="w-full h-2 rounded-full bg-muted overflow-hidden">
            <div 
              className={`h-full transition-all duration-500 ${
                (driverProfile?.total_commission_due || 0) >= ((driverProfile?.max_commission_owed_limit || 500) * 0.9) ? 'bg-red-500' : (driverProfile?.total_commission_due || 0) >= ((driverProfile?.max_commission_owed_limit || 500) * 0.7) ? 'bg-amber-400' : 'bg-gold'
              }`}
              style={{ width: `${Math.min(100, (((driverProfile?.total_commission_due || 0) / (driverProfile?.max_commission_owed_limit || 500)) * 100))}%` }}
            />
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">
            Students pay the platform fee in cash to you on each trip. Accumulate up to K{(driverProfile?.max_commission_owed_limit || 500).toFixed(0)} before remitting payment to Admin.
          </p>
        </GlassCard>

        {/* Earnings Summary - Standalone Clean Icons without Background Boxes */}
        {earnings && (
          <div id="tour-driver-earnings" className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
            <GlassCard className="p-3.5 sm:p-4" hover={false}>
              <div className="flex items-center gap-3">
                <CurrencyCircleDollar weight="duotone" className="w-7 h-7 text-amber-500 shrink-0" />
                <div className="min-w-0">
                  <p className="text-xl sm:text-2xl font-heading font-bold text-foreground leading-tight">
                    K{earnings.today_earnings?.toFixed(2)}
                  </p>
                  <p className="text-[11px] sm:text-xs text-muted-foreground font-medium mt-0.5">Today</p>
                </div>
              </div>
            </GlassCard>

            <GlassCard className="p-3.5 sm:p-4" hover={false}>
              <div className="flex items-center gap-3">
                <Wallet weight="duotone" className={`w-7 h-7 shrink-0 ${driverProfile?.wallet_status === 'restricted' ? 'text-red-500' : 'text-blue-500'}`} />
                <div className="min-w-0">
                  <p className={`text-xl sm:text-2xl font-heading font-bold leading-tight ${driverProfile?.wallet_status === 'restricted' ? 'text-red-500' : 'text-foreground'}`}>
                    K{driverProfile?.wallet_balance?.toFixed(2)}
                  </p>
                  <p className="text-[11px] sm:text-xs text-muted-foreground font-medium mt-0.5">Wallet Bal</p>
                </div>
              </div>
            </GlassCard>

            <GlassCard className="p-3.5 sm:p-4 col-span-2 md:col-span-1" hover={false}>
              <div className="flex items-center gap-3">
                <Car weight="duotone" className="w-7 h-7 text-emerald-500 shrink-0" />
                <div className="min-w-0">
                  <p className="text-xl sm:text-2xl font-heading font-bold text-foreground leading-tight">{earnings.total_rides}</p>
                  <p className="text-[11px] sm:text-xs text-muted-foreground font-medium mt-0.5">Total Rides</p>
                </div>
              </div>
            </GlassCard>
          </div>
        )}

        {/* Pending Rides */}
        {isOnline && pendingRides.length > 0 && (
          <div>
            <h2 className="font-heading font-semibold text-foreground text-lg mb-4">Available Rides</h2>
            <div className="space-y-3">
              {pendingRides.map((ride, idx) => (
                <div key={ride.id} id={idx === 0 ? "tour-driver-incoming-card" : undefined}>
                  <RideCard
                    mode="FULL"
                    request={ride}
                    driverLocation={currentLocation}
                    onAccept={(id) => handleAcceptRide(id)}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {isOnline && pendingRides.length === 0 && !activeRide && (
          <EmptyState
            icon={MapPin}
            title="No rides available"
            description="Stay online, new ride requests will appear here"
          />
        )}
      </div>
    </Layout >
  );
};

function DocumentUploadCard({ title, type, url, onUpload, uploading }) {
  return (
  <div className="relative group">
    <div className={`p-4 rounded-xl border transition-all ${url
      ? 'border-emerald-500/30 bg-emerald-500/5'
      : 'border-white/10 bg-white/5 hover:border-gold/30'
      }`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-muted-foreground">{title}</span>
        {url && <Check className="w-3 h-3 text-emerald-400" />}
      </div>

      {url ? (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
            <Check className="w-4 h-4 text-emerald-400" />
          </div>
          <span className="text-[10px] text-emerald-400 font-medium uppercase">Uploaded</span>
        </div>
      ) : (
        <label className="flex items-center gap-2 cursor-pointer">
          <div className={`w-8 h-8 rounded-lg bg-gold/10 flex items-center justify-center transition-colors ${uploading ? 'animate-pulse' : 'group-hover:bg-gold/20'
            }`}>
            <Upload className="w-4 h-4 text-gold" />
          </div>
          <span className="text-[10px] text-muted-foreground font-medium uppercase group-hover:text-foreground">
            {uploading ? 'Uploading...' : 'Tap to Upload'}
          </span>
          <input
            type="file"
            className="hidden"
            accept="image/*,.pdf"
            onChange={onUpload}
            disabled={uploading}
          />
        </label>
      )}
    </div>
  </div>
  );
}

export default DriverDashboard;
